/**
 * Client for POST /api/cognitory/v2/citation-verify.
 * Sends only the server-issued verification reference — never authoritative spans.
 */

import {
  COGNITORY_V2_CITATION_VERIFY_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import type { CognitoryCitationIdentity } from "../presentation/cognitoryCitationPresentation";

export type CognitoryCitationVerificationResult =
  | {
      kind: "verified";
      requestId: string;
      citationNumber: string;
      /** Server-resolved from verificationReference — authoritative for source open. */
      documentId: string;
      /** Server-resolved from verificationReference — authoritative for source open. */
      documentVersionId: string;
      documentTitle: string;
      citedPassage: string;
      sourceOpenEnabled: boolean;
      pageNumber?: number;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "not_found"
        | "temporary_failure"
        | "invalid";
      requestId: string | null;
    };

function requiredString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export async function verifyCognitoryCitation(input: {
  cognitoryId: string;
  citation: Pick<CognitoryCitationIdentity, "verificationReference">;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryCitationVerificationResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const verificationReference = requiredString(
    input.citation.verificationReference,
  );
  if (!verificationReference) {
    return { kind: "invalid", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(`${base}${COGNITORY_V2_CITATION_VERIFY_PATH}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({
        cognitoryId: input.cognitoryId,
        verificationReference,
      }),
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 404) {
    return { kind: "not_found", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }

  if (!body || typeof body !== "object") {
    return { kind: "invalid", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision === "source_verified") {
    const citationNumber = requiredString(
      (body as { citationNumber?: unknown }).citationNumber,
    );
    const documentId = requiredString(
      (body as { documentId?: unknown }).documentId,
    );
    const documentVersionId = requiredString(
      (body as { documentVersionId?: unknown }).documentVersionId,
    );
    const documentTitle = requiredString(
      (body as { documentTitle?: unknown }).documentTitle,
    );
    const citedPassage = requiredString(
      (body as { citedPassage?: unknown }).citedPassage,
    );
    const sourceOpenEnabled =
      (body as { sourceOpenEnabled?: unknown }).sourceOpenEnabled === true;
    const pageNumberRaw = (body as { pageNumber?: unknown }).pageNumber;
    const pageNumber =
      typeof pageNumberRaw === "number" &&
      Number.isInteger(pageNumberRaw) &&
      pageNumberRaw > 0
        ? pageNumberRaw
        : undefined;
    if (
      !citationNumber ||
      !documentId ||
      !documentVersionId ||
      !documentTitle ||
      !citedPassage
    ) {
      return { kind: "invalid", requestId };
    }
    return {
      kind: "verified",
      requestId: requestId ?? "unknown",
      citationNumber,
      documentId,
      documentVersionId,
      documentTitle,
      citedPassage,
      sourceOpenEnabled,
      ...(pageNumber !== undefined ? { pageNumber } : {}),
    };
  }

  if (decision === "temporary_failure") {
    return { kind: "temporary_failure", requestId };
  }
  if (decision === "forbidden" || decision === "unauthenticated") {
    return {
      kind: decision === "forbidden" ? "forbidden" : "unauthenticated",
      requestId,
    };
  }
  if (decision === "source_not_found") {
    return { kind: "not_found", requestId };
  }

  return { kind: "invalid", requestId };
}
